import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, ModalFooter, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../../components/DropdownMenu';
import FormInput from '../../../../../components/form/FormInput';
import InputLabel from '../../../../../components/form/InputLabel';
import StackedButtonGroup from '../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import LoadingSpinner from '../../../../../components/Spinner';
import { CypressIds } from '../../../../../constants/cypressIds';
import { inAppText } from '../../../../../constants/inAppText';
import { hideModal } from '../../../../../redux/modules/modal';
import { globalRequirementTemplateBuilder } from '../../../../../redux/reducer';
import colors from '../../../../../styles/theme/foundation/colors';

const TemplateDetailsModal = ({ type, templateId, description }) => {
  const isDuplicate = type === 'duplicate';
  const dispatch = useDispatch();
  const history = useHistory();
  const [templateFolder, setTemplateFolder] = useState();
  const [initialFolder, setInitial] = useState();
  const useFormMethods = useForm();
  const { handleSubmit, reset } = useFormMethods;

  useEffect(() => {
    dispatch(globalRequirementTemplateBuilder.getRequirementTemplate(templateId));

    return () => dispatch(globalRequirementTemplateBuilder.resetSelectedTemplate());
  }, [dispatch, templateId]);

  const template = useSelector(globalRequirementTemplateBuilder.selectCurrentRequirementTemplate);
  const folders = useSelector(globalRequirementTemplateBuilder.selectRequirementTemplateFolders).data;
  const folderOptions = isDuplicate ? [...folders, { name: 'Drafts', id: 'drafts' }] : folders;

  useEffect(() => {
    if (template && folders) {
      const defaultFormValues = isDuplicate
        ? { name: `Copy - ${template.name}`, description: `Copy - ${template.description}` }
        : { name: template.name, description: template.description };

      const defaultFolder = !isDuplicate && folders.find(folder => folder.id === template.requirementTemplateFolderId);

      setTemplateFolder(defaultFolder);
      setInitial(defaultFolder.id);
      reset(defaultFormValues);
    }
  }, [template, folders, isDuplicate, reset]);

  const formId = isDuplicate ? 'duplicateTemplate' : 'editTemplate';

  const handleFolderSelect = selectedItem => {
    const selected = { name: selectedItem.label, id: selectedItem.value };
    setTemplateFolder(selected);
  };

  const onSubmit = async data => {
    const saveToDrafts = templateFolder?.id === 'drafts';
    const templateData = {
      name: data.name,
      description: data.description,
      requirement_template_folder_id: saveToDrafts ? null : Number(templateFolder?.id)
    };

    if (isDuplicate) {
      await dispatch(globalRequirementTemplateBuilder.createRequirementTemplate({
        ...templateData,
        requirement_template_blocks: template.requirementTemplateBlocks,
        creator_id: template.creatorId,
        published_at: saveToDrafts ? null : new Date()
      }));

      const folderUrl = history.location.pathname
        .replace(/requirement-templates.*/, `requirement-templates/${templateFolder.id}`);

      history.push(folderUrl);
    } else {
      await dispatch(globalRequirementTemplateBuilder.updateRequirementTemplate(template.id, { ...templateData, updated_at: new Date() }));
      dispatch(globalRequirementTemplateBuilder.listFolderRequirementTemplates(initialFolder));
    }

    dispatch(globalRequirementTemplateBuilder.listRequirementTemplateFolders());
    dispatch(hideModal());
  };

  if (!folders && !template) return <LoadingSpinner />;

  return (
    <Box>
      <Text color={colors.greyScale['500']} mb={7} textAlign="center" textStyle="bodySm">
        {description}
      </Text>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          required
          cypressId={CypressIds.reqTemplatesTemplateDetailsNameInput}
          inputName="name"
          labelName="Template Name"
          placeholder="Template Name"
          useFormMethods={useFormMethods}
          validationRules={{
            required: { value: true, message: 'Template Name is required' },
            maxLength: { value: 191, message: 'Template Name must have fewer than 191 characters' }
          }}
        />
        <FormInput
          inputName="description"
          labelName="Template Description"
          placeholder="Type the template description here"
          toolTip={inAppText.tooltips.templates.templateList.templateOptions.templateDetails.templateDescription}
          type="textArea"
          useFormMethods={useFormMethods}
          validationRules={
            { maxLength: { value: 280, message: 'Template Description must have fewer than 280 characters' } }
          }
        />
        <Box mb="26px">
          <InputLabel
            required
            labelName="Folder"
            toolTip={inAppText.tooltips.templates.templateList.templateOptions.templateDetails.folder}
          />
          <DropdownMenu
            idKey="id"
            labelKey="name"
            options={folderOptions}
            selected={templateFolder?.name}
            width="424px"
            onSelect={handleFolderSelect}
          />
        </Box>
      </form>
      <ModalFooter pt={4}>
        <StackedButtonGroup
          dataAnalyticsId={`template-details-${type}-template`}
          isDisabled={!templateFolder}
          label={`${isDuplicate ? 'Duplicate' : 'Save'} Template`}
          secondaryClick={() => dispatch(hideModal())}
          onClick={handleSubmit(onSubmit)}
        />
      </ModalFooter>
    </Box>
  );
};

TemplateDetailsModal.propTypes = {
  type: PropTypes.string,
  templateId: PropTypes.number.isRequired,
  description: PropTypes.string
};

export default TemplateDetailsModal;
